import {
  List, Edit, Create, SimpleForm, TextInput, SelectInput,
  ReferenceInput, AutocompleteInput, NumberInput, required,
  useDataProvider, useNotify, useRefresh, useListContext,
} from 'react-admin';
import {
  Stack, Box, Button, CircularProgress, IconButton, Typography, LinearProgress, Tooltip,
} from '@mui/material';
import { useRef, useState, useCallback } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CollectionsIcon from '@mui/icons-material/Collections';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { ImageUploadInput } from '../../components/ImageUploadInput.jsx';
import { ReorderableDatagrid, imageCell } from '../../components/ReorderableDatagrid.jsx';
import { uploadFile } from '../../providers/dataProvider.js';
import { compressImage, formatBytes } from '../../lib/compressImage.js';
import { tokens } from '../../theme.js';

const TILE_SIZES = [
  { id: 'regular', name: 'Regular (1×1)' },
  { id: 'wide',    name: 'Wide (2×1)' },
  { id: 'tall',    name: 'Tall (1×2)' },
];

const filters = [
  <ReferenceInput key="albumId" source="albumId" reference="gallery-albums" alwaysOn>
    <AutocompleteInput optionText="title" label="Album" />
  </ReferenceInput>,
];

// ─────────────────────────────────────────────────────────────────────
// Batch uploader with adaptive client-side compression
// ─────────────────────────────────────────────────────────────────────

const STAGE_LABEL = {
  queued:      'Queued',
  compressing: 'Compressing',
  uploading:   'Uploading',
  done:        'Done',
  error:       'Error',
};

const STAGE_COLOR = {
  queued:      tokens.ink4,
  compressing: tokens.gold700,
  uploading:   tokens.green700,
  done:        tokens.green700,
  error:       '#b54033',
};

/**
 * Run async tasks with bounded concurrency. Yields each task's result in the
 * same order as the input array; errors are caught and surfaced as { error }.
 */
async function pMap(items, mapper, concurrency = 3) {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      try { results[i] = await mapper(items[i], i); }
      catch (err) { results[i] = { error: err }; }
    }
  });
  await Promise.all(workers);
  return results;
}

function ProgressRow({ item, onRemove }) {
  const isTerminal = item.stage === 'done' || item.stage === 'error';
  const savedPct = item.compressedSize && item.originalSize
    ? Math.max(0, Math.round((1 - item.compressedSize / item.originalSize) * 100))
    : 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr auto 28px',
        gap: 1.25,
        alignItems: 'center',
        p: 1,
        borderTop: `1px solid ${tokens.line2}`,
        opacity: item.stage === 'done' ? 0.85 : 1,
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          width: 40, height: 40,
          borderRadius: 0.5,
          background: tokens.cream2,
          backgroundImage: item.previewUrl ? `url('${item.previewUrl}')` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
        }}
      />

      {/* Filename + sizes + progress bar */}
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            sx={{ fontSize: 13, color: tokens.ink, fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {item.file.name}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.25 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: STAGE_COLOR[item.stage],
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {STAGE_LABEL[item.stage]}
            {item.stage === 'compressing' && item.percent != null && ` ${item.percent}%`}
          </Typography>
          <Typography sx={{ fontSize: 11, color: tokens.ink4 }}>
            {formatBytes(item.originalSize)}
            {item.stage !== 'queued' && item.compressedSize && (
              <>
                <span style={{ color: tokens.ink4, margin: '0 4px' }}>→</span>
                <span style={{ color: tokens.green700, fontWeight: 600 }}>
                  {formatBytes(item.compressedSize)}
                </span>
                {savedPct > 0 && (
                  <span style={{ color: tokens.gold700, marginLeft: 6, fontWeight: 600 }}>
                    −{savedPct}%
                  </span>
                )}
              </>
            )}
            {item.skipped && (
              <span style={{ color: tokens.ink4, marginLeft: 6, fontStyle: 'italic' }}>
                · already small enough
              </span>
            )}
          </Typography>
        </Stack>
        {item.stage === 'error' && item.error && (
          <Typography sx={{ fontSize: 11, color: '#b54033', mt: 0.5 }}>
            {item.error}
          </Typography>
        )}
        {(item.stage === 'compressing' || item.stage === 'uploading') && (
          <LinearProgress
            variant={item.stage === 'compressing' && item.percent != null ? 'determinate' : 'indeterminate'}
            value={item.percent || 0}
            sx={{
              mt: 0.5, height: 3, borderRadius: 2,
              background: tokens.cream2,
              '& .MuiLinearProgress-bar': { background: STAGE_COLOR[item.stage] },
            }}
          />
        )}
      </Box>

      {/* Status icon */}
      <Box sx={{ width: 22, display: 'flex', justifyContent: 'center' }}>
        {item.stage === 'done'  && <CheckCircleIcon sx={{ fontSize: 18, color: tokens.green700 }} />}
        {item.stage === 'error' && <ErrorOutlineIcon sx={{ fontSize: 18, color: '#b54033' }} />}
      </Box>

      {/* Remove (only available before processing or after) */}
      <IconButton
        size="small"
        onClick={() => onRemove(item.id)}
        disabled={!isTerminal && item.stage !== 'queued'}
        title={isTerminal ? 'Dismiss' : 'Cancel from queue'}
        sx={{ p: 0.5 }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

function BatchUploadButton() {
  const inputRef = useRef(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { filterValues } = useListContext();
  const albumId = filterValues?.albumId;

  const [items, setItems] = useState([]); // { id, file, previewUrl, stage, percent, originalSize, compressedSize, skipped, error, url }
  const [busy, setBusy] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const updateItem = useCallback((id, patch) => {
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const removeItem = useCallback((id) => {
    setItems((cur) => {
      const removed = cur.find((it) => it.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return cur.filter((it) => it.id !== id);
    });
  }, []);

  const clearAll = () => {
    items.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    setItems([]);
  };

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    if (!albumId) {
      notify('Pick an album in the filter first.', { type: 'warning' });
      return;
    }

    // Build queue with previews
    const newItems = picked.map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      file,
      previewUrl: URL.createObjectURL(file),
      stage: 'queued',
      percent: 0,
      originalSize: file.size,
      compressedSize: null,
      skipped: false,
      error: null,
      url: null,
    }));
    setItems((prev) => [...prev, ...newItems]);

    setBusy(true);
    let okCount = 0;
    let failCount = 0;

    await pMap(
      newItems,
      async (item) => {
        try {
          // ── 1. compress ───────────────────────────────────────
          updateItem(item.id, { stage: 'compressing', percent: 0 });
          const result = await compressImage(item.file, {
            onProgress: (pct) => updateItem(item.id, { percent: pct }),
          });
          updateItem(item.id, {
            compressedSize: result.compressedSize,
            skipped: result.skipped,
            percent: 100,
          });

          // ── 2. upload ─────────────────────────────────────────
          updateItem(item.id, { stage: 'uploading' });
          const uploaded = await uploadFile(result.file);

          // ── 3. create the gallery_image row ───────────────────
          await dataProvider.create('gallery-images', {
            data: { albumId, image: uploaded.url, caption: '', tileSize: 'regular' },
          });

          updateItem(item.id, { stage: 'done', url: uploaded.url });
          okCount += 1;
        } catch (err) {
          console.error('upload failed', item.file.name, err);
          updateItem(item.id, { stage: 'error', error: err?.message || String(err) });
          failCount += 1;
        }
      },
      3, // concurrency: 3 files in flight at once
    );

    setBusy(false);
    notify(
      `Uploaded ${okCount} image${okCount === 1 ? '' : 's'}${failCount ? `, ${failCount} failed` : ''}`,
      { type: failCount ? 'warning' : 'success' },
    );
    refresh();
  };

  const totalOriginal = items.reduce((s, it) => s + (it.originalSize || 0), 0);
  const totalCompressed = items.reduce((s, it) => s + (it.compressedSize || 0), 0);
  const allDone = items.length > 0 && items.every((it) => it.stage === 'done' || it.stage === 'error');

  return (
    <Box sx={{ width: '100%', maxWidth: 640 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        <input type="file" accept="image/*" multiple hidden ref={inputRef} onChange={onChange} />
        <Button
          size="small"
          variant="outlined"
          startIcon={busy ? <CircularProgress size={14} /> : <CloudUploadIcon />}
          onClick={onPick}
          disabled={busy || !albumId}
        >
          {busy ? 'Working…' : 'Batch upload'}
        </Button>
        {items.length > 0 && allDone && (
          <Button size="small" variant="text" onClick={clearAll}>Clear list</Button>
        )}
      </Stack>

      {/* Per-file progress panel */}
      {items.length > 0 && (
        <Box
          sx={{
            mt: 1,
            border: `1px solid ${tokens.line}`,
            borderRadius: 1,
            background: tokens.paper,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.25, background: tokens.cream, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, color: tokens.ink2, fontWeight: 700, letterSpacing: '0.05em' }}>
              {items.length} file{items.length === 1 ? '' : 's'} · {items.filter((i) => i.stage === 'done').length} done · {items.filter((i) => i.stage === 'error').length} failed
            </Typography>
            {totalCompressed > 0 && totalOriginal > 0 && (
              <Tooltip title="Total bytes saved by compression">
                <Typography sx={{ fontSize: 12, color: tokens.green700, fontWeight: 600 }}>
                  {formatBytes(totalOriginal)} → {formatBytes(totalCompressed)}{' '}
                  ({Math.round((1 - totalCompressed / totalOriginal) * 100)}% saved)
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
            {items.map((item) => (
              <ProgressRow key={item.id} item={item} onRemove={removeItem} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const ListActions = () => (
  <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
    <BatchUploadButton />
  </Box>
);

const ImageList = () => (
  <List
    sort={{ field: 'sortOrder', order: 'ASC' }}
    perPage={100}
    pagination={false}
    filters={filters}
    actions={<ListActions />}
    empty={false}
  >
    <ReorderableDatagrid
      columns={[
        { key: 'image',    header: 'Image',     width: 100, render: (r) => imageCell(r.image) },
        { key: 'caption',  header: 'Caption',               render: (r) => r.caption || '—' },
        { key: 'tileSize', header: 'Tile size', width: 120, render: (r) => r.tileSize },
      ]}
    />
  </List>
);

const ImageForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <ReferenceInput source="albumId" reference="gallery-albums">
      <AutocompleteInput optionText="title" validate={required()} fullWidth />
    </ReferenceInput>
    <ImageUploadInput source="image" label="Image" validate={required()} />
    <TextInput source="caption" multiline minRows={2} fullWidth />
    <Stack direction="row" spacing={2}>
      <SelectInput source="tileSize" choices={TILE_SIZES} defaultValue="regular" sx={{ minWidth: 200 }} />
      <NumberInput source="sortOrder" defaultValue={0} />
    </Stack>
  </Stack>
);

const ImageEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm><ImageForm /></SimpleForm>
  </Edit>
);
const ImageCreate = () => (
  <Create redirect="list">
    <SimpleForm><ImageForm /></SimpleForm>
  </Create>
);

export default {
  list: ImageList,
  edit: ImageEdit,
  create: ImageCreate,
  icon: CollectionsIcon,
  options: { label: 'Gallery images' },
};
